<template>
  <div class="payment-vault-info box">
    <div v-if="this.error !== null" class="is-flex is-align-items-center is-flex-direction-column">
      <h1 class="title">Error!</h1>
      <pre>{{ JSON.stringify(this.error, null, 2) }}</pre>
    </div>
    <div v-else-if="this.contractData === null" class="loader-wrapper">
      <div class="loader is-large is-primary"></div>
    </div>
    <div v-else>
      <div class="is-flex is-align-items-center is-justify-content-space-between is-size-4">
        <div class="is-flex is-align-items-center">
          <img class="token-icon" :src="this.tokenMetadata.icon">
          <p class="has-text is-marginless pr-2">{{ this.tokenMetadata.symbol }} Payment Vault</p>
        </div>
        <div class="action-buttons">
          <a target="_blank" rel="noopener" class="button is-primary is-small" :href="bcdLink(this.contractAddress)">View Vault Contract</a>
          <a target="_blank" rel="noopener" class="button is-primary is-small" :href="bcdLink(this.contractData.tokenContractAddress)">View Token Contract</a>
        </div>
      </div>

      <nav class="level top-stats">
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Emissions Per Block</p>
            <p class="title">{{ numberWithCommas(contractData.amountPerBlock.div(tokenMantissa).toFixed(2)) }} <span class="help">{{ this.tokenMetadata.symbol }}</span></p>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Starting Block</p>
            <p class="title">
              <a rel="noopener" target="_blank" :href="`https://tzstats.com/${contractData.startBlock}`">{{ numberWithCommas(contractData.startBlock) }}</a>
            </p>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Current Block</p>
            <p class="title">
              <a rel="noopener" target="_blank" :href="`https://tzstats.com/${$store.currentBlockHeight}`">{{ numberWithCommas($store.currentBlockHeight) }}</a>
            </p>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Contract Balance</p>
            <p class="title">{{ numberWithCommas(paymentVaultContractBalanceFormatted.toFixed(2)) }} <span class="help">{{ this.tokenMetadata.symbol }}</span></p>
          </div>
        </div>
      </nav>

      <nav class="level bottom-stats">
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Previously Withdrawn</p>
            <p class="title">
              {{ numberWithCommas(contractData.amountWithdrawn.div(tokenMantissa).toFixed(2)) }} <span class="help">{{ this.tokenMetadata.symbol }}</span>
            </p>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Total Accrued</p>
            <p class="title">
              {{ numberWithCommas(totalAccrued.toFixed(2)) }} <span class="help">{{ this.tokenMetadata.symbol }}</span>
            </p>
          </div>
        </div>
        <div class="level-item has-text-centered">
          <div>
            <p class="heading">Available To Withdraw</p>
            <p class="title">
              {{ numberWithCommas(availableToWithdraw.toFixed(2)) }} <span class="help">{{ this.tokenMetadata.symbol }}</span>
            </p>
          </div>
        </div>
      </nav>

      <div v-if="this.contractData.owner === $store.walletAddress">
        <div class="field is-horizontal withdraw-input">
          <div class="field-label is-normal">
            <label class="label">Withdraw</label>
          </div>
          <div class="field-body">
            <div class="field has-addons">
              <div class="control is-expanded">
                <input
                  v-model="withdrawInput" class="input">
                <div
                  @click="withdrawInput = availableToWithdraw.toFixed(2, 1)"
                  class="max-button heading"
                >
                  Max
                </div>
              </div>
              <p class="control">
                <a class="button is-static has-text-weight-bold">
                  {{ this.tokenMetadata.symbol }}
                </a>
              </p>
            </div>
          </div>
        </div>
        <div class="field withdraw-button">
          <div class="control">
            <button
              :class="{
                'is-loading': loading,
                'is-disabled': this.contractData.owner !== $store.walletAddress
              }"
              @click="withdrawFunds"
              :disabled="!withdrawInputIsValid || loading"
              class="button is-primary has-text-weight-bold action-button">
              Withdraw {{ this.tokenMetadata.symbol }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BigNumber from "bignumber.js";
import {tzip12} from "@taquito/tzip12";
import Mixins from "@/mixins";

export default {
  name: "PaymentVaultInfo",
  props: ['contractAddress'],
  mixins: [Mixins],
  async mounted(){
    try {
      await this.updateData()
    } catch (e){
      this.error = e
    }
  },
  data(){
    return {
      contractData: null,
      tokenMetadata: null,
      tokenData: null,
      loading: false,
      paymentVaultContractBalance: null,
      withdrawInput: '',
      error: null,
    }
  },
  methods:{
    async updateData(){
      const vaultContract = await this.$store.tezosToolkit.wallet.at(this.contractAddress)
      const vaultStorage = await vaultContract.storage()

      const tokenContract = await this.$store.tezosToolkit.wallet.at(vaultStorage.tokenContractAddress, tzip12)
      const tokenContractStorage = await tokenContract.storage()

      if (vaultStorage.tokenContractAddress === this.$store.contracts.TOKEN){
        this.tokenMetadata = {
          decimals: 18,
          icon: "https://kolibri-data.s3.amazonaws.com/logo.png",
          name: "Kolibri USD",
          symbol: "kUSD",
          token_id: 0,
        }
      } else {
        this.tokenMetadata = await tokenContract.tzip12().getTokenMetadata(0);
      }

      let currentHoldings = await tokenContractStorage.balances.get(this.contractAddress) || new BigNumber(0)

      // kDAO token just stores a nat, other tokens (like kUSD) usually store a map
      if (vaultStorage.tokenContractAddress !== this.$store.contracts.DAO_TOKEN){
        currentHoldings = currentHoldings.balance
      }

      this.paymentVaultContractBalance =  currentHoldings
      this.tokenData = tokenContractStorage
      this.contractData = vaultStorage
    },
    async withdrawFunds(){
      this.loading = true
      try {
        const contract = await this.$store.tezosToolkit.wallet.at(this.contractAddress)
        const vestedKDAORaw = new BigNumber(this.withdrawInput).times(this.tokenMantissa)

        const opResult = await contract.methods.withdraw(vestedKDAORaw).send()
        await opResult.confirmation(1)

        this.withdrawInput = ''
        this.$eventBus.$emit('update-balances')
        this.contractData = null
        await this.updateData()
      } catch(e) {
        console.log(e)
      } finally {
        this.loading = false
      }
    }
  },
  computed: {
    withdrawInputIsValid(){
      if (this.withdrawInput.length === 0){
        return false
      }

      if (new BigNumber(this.withdrawInput).isGreaterThan(this.availableToWithdraw)){
        return false
      }

      if (new BigNumber(this.withdrawInput).isZero()){
        return false
      }

      return true
    },
    paymentVaultContractBalanceFormatted(){
      return this.paymentVaultContractBalance.dividedBy(this.tokenMantissa)
    },
    tokenMantissa(){
      return new BigNumber(10).pow(this.tokenMetadata.decimals)
    },
    currentPaymentVaults(){
      return this.$store.contracts.PAYMENT_VAULTS[this.$store.walletAddress]
    },
    totalAccrued(){
      const accrued = this.contractData.amountPerBlock
        .times(
          new BigNumber(this.$store.currentBlockHeight).minus(this.contractData.startBlock)
        ).div(this.tokenMantissa)
      if (accrued.isLessThan(0)){
        return new BigNumber(0)
      } else {
        return accrued
      }
    },
    availableToWithdraw(){
      const maxPotentialKDAO = this.contractData.amountPerBlock
        .times(
          new BigNumber(this.$store.currentBlockHeight).minus(this.contractData.startBlock)
        )
        .minus(this.alreadyWithdrawn.times(this.tokenMantissa))
      const available = BigNumber.minimum(maxPotentialKDAO, this.paymentVaultContractBalance)
        .dividedBy(this.tokenMantissa)

      if (available.isLessThan(0)){
        return new BigNumber(0)
      } else {
        return available
      }
    },
    alreadyWithdrawn(){
      return this.contractData.amountWithdrawn.dividedBy(this.tokenMantissa)
    },
    amtPerBlock(){
      return this.contractData.amountPerBlock.dividedBy(this.tokenMantissa)
    },
  },
  watch: {
    '$store.wallet': async function() {
      this.withdrawInput = ''
      this.contractData = null
      await this.updateData()
    }
  }
}
</script>

<style lang="scss">
@import '../assets/sass/globals';
.payment-vault-info{
  pre{
    width: 100%;
  }
  .title{
    color: $grey-dark !important;
    a{
      border-bottom: 2px solid $grey;
      &:hover{
        color: $primary !important;
        border-color: $primary !important;
      }
    }
  }
  .loader-wrapper{
    min-height: 23rem;
  }
  .token-icon{
    width: 3rem;
    height: 3rem;
    margin-right: 0.5rem;
  }
  .action-buttons{
    display: flex;
    gap: 0.5rem;
  }
  .top-stats, .bottom-stats{
    padding-top: 1rem;
    .title{
      color: $grey-dark;
    }
  }
  .bottom-stats{
    margin-bottom: 2rem;
  }
  .control{
    &:hover{
      .max-button{
        opacity: 1;
      }
    }
    input:focus + .max-button{
      opacity: 1;
    }
  }
  .max-button{
    position: absolute;
    top: 0.75rem;
    color: $primary;
    right: .5rem;
    font-weight: bold;
    cursor: pointer;
    border-bottom: 1px solid transparent;
    z-index: 9;
    opacity: 0;
    transition: opacity 250ms linear;
    &:hover{
      border-bottom: 1px solid $primary;
    }
  }
  .withdraw-input{
    max-width: 30rem;
    margin: auto;
  }
  .withdraw-button{
    text-align: center;
    margin-top: 2rem;
  }
}
</style>
